import { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAgentStore } from '../../store/agentStore.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { api, setVistaToken } from '../../lib/api.js';
import StatusBadge from '../shared/StatusBadge.jsx';
import ExportButton from '../shared/ExportButton.jsx';
import AlertCountBadge from '../alerts/AlertCountBadge.jsx';

const ROLE_LABELS = {
  org_admin: 'Admin',
  org_ro: 'Read Only',
  deactivated: 'Deactivated',
};

const ROLE_LEVEL = {
  deactivated: -1,
  org_ro: 1,
  org_admin: 2,
};

function SettingsIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

// v0.13.3: Alerts moved from sidebar to a bell icon next to the
// settings gear. Carries the same active-count badge.
function BellIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path d="M7 8a3 3 0 100-6 3 3 0 000 6zM14.5 9a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM1.615 16.428a1.224 1.224 0 01-.569-1.175 6.002 6.002 0 0111.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 017 18a9.953 9.953 0 01-5.385-1.572zM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 00-1.588-3.755 4.502 4.502 0 015.874 2.636.818.818 0 01-.36.98A7.465 7.465 0 0114.5 16z" />
    </svg>
  );
}

function UserCircleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-5.5-2.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0zM10 12a5.99 5.99 0 00-4.793 2.39A6.483 6.483 0 0010 16.5a6.483 6.483 0 004.793-2.11A5.99 5.99 0 0010 12z" clipRule="evenodd" />
    </svg>
  );
}

function LogoutIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M3 4.25A2.25 2.25 0 015.25 2h5.5A2.25 2.25 0 0113 4.25v2a.75.75 0 01-1.5 0v-2a.75.75 0 00-.75-.75h-5.5a.75.75 0 00-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 00.75-.75v-2a.75.75 0 011.5 0v2A2.25 2.25 0 0110.75 18h-5.5A2.25 2.25 0 013 15.75V4.25z" clipRule="evenodd" />
      <path fillRule="evenodd" d="M19 10a.75.75 0 00-.75-.75H8.704l1.048-.943a.75.75 0 10-1.004-1.114l-2.5 2.25a.75.75 0 000 1.114l2.5 2.25a.75.75 0 101.004-1.114l-1.048-.943h9.546A.75.75 0 0019 10z" clipRule="evenodd" />
    </svg>
  );
}

function EditIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
    </svg>
  );
}

export default function Header({ connected, agentStatus }) {
  const { agentId, setAgentId } = useAgentStore();
  const { user, role, orgName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const userLevel = ROLE_LEVEL[role] ?? 0;
  const canSeeSettings = userLevel >= ROLE_LEVEL.org_admin;

  const { data: agents } = useQuery({
    queryKey: ['agents-list'],
    queryFn: () => api.get('/agents'),
  });

  useEffect(() => {
    if (agents === undefined) return;
    const agentList = Array.isArray(agents) ? agents : [];
    const agentExists = agentList.length > 0 && agentList.some((a) => a.agent_id === agentId);
    if (agentId && !agentExists) {
      setAgentId(agentList.length > 0 ? agentList[0].agent_id : null);
    } else if (!agentId && agentList.length > 0) {
      setAgentId(agentList[0].agent_id);
    }
  }, [agentId, agents, setAgentId]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Close dropdown on navigation
  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);

  function handleSignOut() {
    setVistaToken(null);
    window.location.href = '/cdn-cgi/access/logout';
  }

  const initial = (user?.name || user?.email || '?')[0].toUpperCase();

  return (
    <header className="flex h-14 items-center justify-between border-b border-gray-200 bg-white px-6">
      {/* Left: Agent selector */}
      <div className="flex items-center gap-3">
        <label htmlFor="agent-select" className="text-sm font-medium text-gray-500">
          Agent
        </label>
        <select
          id="agent-select"
          value={agentId || ''}
          onChange={(e) => setAgentId(e.target.value || null)}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Select agent...</option>
          {(Array.isArray(agents) ? agents : []).map((agent) => (
            <option key={agent.agent_id} value={agent.agent_id}>
              {agent.name} ({agent.status})
            </option>
          ))}
        </select>
        {agentId ? (() => {
          // Agent online = heartbeat recent (source of truth is the agents
          // list from /api/v1/agents, which is updated by cloud's heartbeat
          // handler). Browser↔cloud WS is a separate concern — shown as
          // "Live" when the WS has also delivered an agent.status=online
          // push. See vista#180.
          const selectedAgent = (Array.isArray(agents) ? agents : []).find((a) => a.agent_id === agentId);
          const agentOnline = selectedAgent?.status === 'active' || agentStatus === 'online';
          const wsLive = connected && agentStatus === 'online';
          if (!agentOnline) {
            return <StatusBadge status="warning" label="Agent Offline" />;
          }
          return wsLive
            ? <StatusBadge status="active" label="Live" />
            : <StatusBadge status="active" label="Online" />;
        })() : (
          <StatusBadge status="unknown" label="No Agent" />
        )}
      </div>

      {/* Right: Export + admin icons + user menu */}
      <div className="flex items-center gap-1">
        <ExportButton />

        <div className="mx-2 h-6 w-px bg-gray-200" />

        {/* v0.13.3: Alerts bell — moved here from the sidebar. The
            AlertCountBadge overlays the bottom-right of the bell as
            a small red dot with the active alert count. Visible to
            all roles (anyone who can see the dashboard should see
            alerts), unlike Settings which is org_admin-only. */}
        <button
          onClick={() => navigate('/alerts')}
          title="Alerts"
          className={`relative rounded-md p-1.5 transition-colors ${
            location.pathname === '/alerts'
              ? 'bg-blue-50 text-blue-600'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
          }`}
        >
          <BellIcon className="h-5 w-5" />
          <span className="absolute -right-0.5 -top-0.5">
            <AlertCountBadge />
          </span>
        </button>

        {/* Admin icon links */}
        {canSeeSettings && (
          <button
            onClick={() => navigate('/settings')}
            title="Settings"
            className={`rounded-md p-1.5 transition-colors ${
              location.pathname === '/settings'
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            <SettingsIcon className="h-5 w-5" />
          </button>
        )}
        {canSeeSettings && (
          <button
            onClick={() => navigate('/users')}
            title="Users"
            className={`rounded-md p-1.5 transition-colors ${
              location.pathname === '/users'
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            <UsersIcon className="h-5 w-5" />
          </button>
        )}

        <div className="mx-2 h-6 w-px bg-gray-200" />

        {/* User avatar + dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen((prev) => !prev)}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-medium text-white transition-shadow hover:ring-2 hover:ring-blue-300"
            title={user?.name || user?.email || 'Profile'}
          >
            {initial}
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-lg">
              {/* User info */}
              <div className="border-b border-gray-100 px-4 py-3">
                <p className="text-sm font-semibold text-gray-900">{user?.name || 'Unknown'}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <UserCircleIcon className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-xs text-gray-500">{ROLE_LABELS[role] || role}</span>
                </div>
                {orgName && (
                  <p className="mt-1 text-xs text-gray-400">{orgName}</p>
                )}
              </div>

              {/* Actions */}
              <div className="py-1">
                <button
                  onClick={() => navigate('/settings?tab=preferences')}
                  className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  <EditIcon className="h-4 w-4 text-gray-400" />
                  Edit Profile
                </button>
              </div>

              <div className="border-t border-gray-100">
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  <LogoutIcon className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
